import { ComponentChildren } from 'preact';
import { useRef, useLayoutEffect } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { usePress } from '../hooks/UNSAFE_usePress';
import { Text } from '../UNSAFE_Text';
import { Flex } from '../UNSAFE_Flex';
import { useTabBarContext } from './useTabBarContext';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TabBarItemVariantOptions } from './themes/TabBarItemStyles.css';
import { TabBarItemRedwoodTheme } from './themes/redwood/TabBarItemTheme';

export type TabBarItemProps<K extends string | number> = {
  /**
   * Key of the TabBarItem
   */
  itemKey: K;
  /**
   * Label of the TabBarItem
   */
  label?: string;
  /**
   * The content to be rendered before the tab label
   */
  startContent?: ComponentChildren;
  /**
   * The content to be rendered after the tab label
   */
  endContent?: ComponentChildren;
};

const ITEM_STYLE_CLASS = 'oj-c-tabbar-item';

export function TabBarItem<K extends string | number>({
  itemKey,
  label,
  startContent,
  endContent
}: TabBarItemProps<K>) {
  const tabBarItemRef = useRef<HTMLDivElement | null>(null);
  const {
    onSelectionChange,
    selection,
    currentKey,
    showFocusRing,
    layout,
    isActiveElement,
    isFirstItemRendered
  } = useTabBarContext();

  const isCurrent = currentKey === itemKey;
  let tabIndex = isCurrent ? 0 : -1;
  //If currentKey is undefined, in order to tab to a TabBarItem, the first TabBarItem should set its tabindex to 0
  if (currentKey === undefined && isFirstItemRendered && !isFirstItemRendered.current) {
    tabIndex = 0;
    isFirstItemRendered.current = true;
  }

  //Selection
  const handlePress = () => {
    onSelectionChange?.({ value: itemKey as K });
  };

  const { pressProps } = usePress(handlePress);

  const isSelected = selection === itemKey;

  useLayoutEffect(() => {
    if (isActiveElement?.() && isCurrent) {
      tabBarItemRef?.current?.focus();
    }
  }, [isCurrent, isActiveElement]);

  const {
    classes,
    styles: { tabBarItemStartContent, tabBarItemEndContent }
  } = useComponentTheme<TabBarItemVariantOptions>(TabBarItemRedwoodTheme, {
    selection: isSelected ? 'isSelected' : 'notSelected',
    current: isCurrent ? 'isCurrent' : 'notCurrent',
    focusRing: showFocusRing ? 'showFocusRing' : 'noFocusRing'
  });

  const tabBarItem = classNames([classes, ITEM_STYLE_CLASS]);

  const tabBarItemStartContentClasses = classNames([tabBarItemStartContent]);
  const tabBarItemEndContentClasses = classNames([tabBarItemEndContent]);

  //instead of specifying flex: '1 0 auto' in cssProps var
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const flexDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto'
  });

  return (
    <div
      data-oj-key={itemKey}
      class={tabBarItem}
      role="tab"
      ref={tabBarItemRef}
      {...pressProps}
      aria-selected={isSelected}
      tabIndex={tabIndex}
      style={flexDimensions}>
      <Flex
        height="11x"
        minWidth="14x"
        direction="row"
        gap="2x"
        align="center"
        justify="center"
        flex="1 1 auto">
        {startContent && <span class={tabBarItemStartContentClasses}>{startContent}</span>}
        {label && (
          <Text size="md" weight="semiBold">
            {label}
          </Text>
        )}
        {endContent && <span class={tabBarItemEndContentClasses}>{endContent}</span>}
      </Flex>
    </div>
  );
}
